/**
 * /field/pf/jobs/:id/log/:workDate — the hand's daily log for a day
 * (GET / PUT /pipeline-facility/field/jobs/:id/daily-logs/:workDate,
 * fieldiq-api#1200). An upsert by day: the same Save writes it the first time
 * and updates it after. SNAKE_CASE both ways.
 *
 * CREW ONLY (403 not_on_crew, said as a sentence). The form is the office's
 * (LogEditor) on the job's own crew and units. A unit with registered
 * equipment offers "File a pre-use check": the shared equipment check flow,
 * which files the check WITHOUT a job and then brings the hand back here
 * (`returnTo`), where the check shows in the unit's picker to link.
 *
 * A save with a `warnings` entry (meter_below_previous) went through and says
 * so in an amber notice.
 */
import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { usePfFieldJob, usePfFieldLog, usePfPreUseChecks, useSavePfFieldLog } from '../../../hooks/usePipelineFacility';
import { pfDay, pfRefusal, pfWarningText } from '../../../lib/pipeline-facility';
import type { PfFieldLogWarning } from '../../../types/pipeline-facility';
import { PtFieldCard, PtFieldPage, PtFieldQueryState, PtRefusal } from '../../pressure-testing/field/PtFieldBits';
import { LogEditor } from '../LogEditor';
import { fieldLogOptions, fieldLogValues } from '../logOptions';
import { notOnCrewMessage } from './PfFieldJobScreen';

export default function PfFieldLogScreen() {
  const { id, workDate } = useParams<{ id: string; workDate: string }>();
  const location = useLocation();
  const jobQuery = usePfFieldJob(id);
  const logQuery = usePfFieldLog(id, workDate);
  const checks = usePfPreUseChecks(id, workDate);
  const save = useSavePfFieldLog();
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<PfFieldLogWarning[]>([]);
  const [formKey, setFormKey] = useState(0);

  const job = jobQuery.data?.data ?? null;
  const log = logQuery.data?.data ?? null;
  const meta = jobQuery.data?.meta ?? logQuery.data?.meta ?? null;
  const refused = notOnCrewMessage(jobQuery) ?? notOnCrewMessage(logQuery);
  const loggable = job?.status === 'scheduled' || job?.status === 'in_progress';
  const options = job ? fieldLogOptions(job) : { crew: [], units: [] };
  const unitName = (equipmentId?: string) => options.units.find((u) => u.kind === 'owned' && u.equipmentId === equipmentId)?.name ?? null;

  return (
    <PtFieldPage title={`Daily log · ${pfDay(workDate)}`} backTo={id ? `/field/pf/jobs/${id}` : '/field/pf'} testId="pf-field-log">
      {refused ? (
        <PtRefusal message={refused} testId="pf-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={jobQuery} subject="this job">
          <PtFieldQueryState query={logQuery} subject="this daily log">
          {!job ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-fieldiq-text">
                <span className="font-mono font-extrabold">{job.job_number}</span> · {job.customer_name || '—'} · {job.site_name || '—'}
              </p>
              {warnings.length > 0 && (
                <div role="status" data-testid="pf-log-warnings" className="flex gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-amber" aria-hidden="true" />
                  <ul className="space-y-1">
                    {warnings.map((w, i) => (
                      <li key={i}>{pfWarningText(w, unitName(w.equipment_id))}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!loggable && (
                <PtRefusal message={job.status === 'draft' ? 'This job is still a draft. The office schedules it before work is logged.' : 'This job is completed or cancelled. Its logs can no longer be changed.'} />
              )}
              <PtFieldCard className="px-4 py-4">
                <LogEditor
                  key={`${log?.id ?? 'new'}-${formKey}`}
                  workDate={workDate ?? ''}
                  values={fieldLogValues(log)}
                  crewOptions={options.crew}
                  unitOptions={options.units}
                  checks={checks.data?.data ?? []}
                  chainageUnit={meta?.units?.chainage ?? null}
                  pending={save.isPending}
                  error={error}
                  readOnly={!loggable}
                  submitLabel={log ? 'Save log' : 'Save today’s log'}
                  fileCheckState={(u) => ({
                    to: '/field/equipment/check',
                    state: { equipment: { id: u.equipmentId, name: u.name, unit_number: u.detail }, returnTo: location.pathname },
                  })}
                  onSubmit={(body) => {
                    setError(null);
                    save.mutate(
                      { jobId: job.id, day: workDate as string, body },
                      {
                        onSuccess: (res) => {
                          toast.success('Daily log saved');
                          setWarnings(res.warnings);
                          // The write's invalidation has re-read the log by now
                          // (useSavePfFieldLog awaits it), so rebuild the form from it.
                          setFormKey((k) => k + 1);
                        },
                        onError: (err) => setError(pfRefusal(err, 'The daily log could not be saved.')),
                      },
                    );
                  }}
                />
              </PtFieldCard>
            </div>
          )}
          </PtFieldQueryState>
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
